/**
 * StatusTimeline — Reusable dynamic lifecycle component.
 *
 * Renders a timeline of status phases with expandable details.
 * Used by both payment detail and execution detail pages.
 * The phaseDetails field is a JSON string that can contain:
 * - Simple key-value pairs (rendered as KeyValuePairs)
 * - Arrays (rendered as inline tables)
 * - Nested objects (rendered recursively as key-value)
 */
import { useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';
import { PhaseDetailsRenderer } from './PhaseDetailsRenderer';

/** A single entry in the status timeline. */
export interface TimelineEntry {
  status: string;
  state: string;
  enteredAt: string;
  exitedAt: string | null;
  statusLatencyMs: number | null;
  latencyFromPreviousMs: number | null;
  cumulativeLatencyMs: number;
  phaseDetails: string | null;
}

interface StatusTimelineProps {
  /** Section title (e.g. "Payment Lifecycle", "Execution Lifecycle"). */
  title: string;
  /** Optional description below the title. */
  description?: string;
  /** Ordered timeline entries. */
  timeline: TimelineEntry[];
}

/** Maps state string to StatusIndicator type. */
const stateToType = (state: string): 'success' | 'error' | 'in-progress' | 'warning' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  if (state === 'PROCESSING' || state === 'IN_PROGRESS') return 'in-progress';
  return 'warning';
};

export const StatusTimeline = ({ title, description, timeline }: StatusTimelineProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (timeline.length === 0) return null;

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const timelineList = (
    <SpaceBetween size="xs">
      {timeline.map((entry, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {/* Vertical connector line */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24 }}>
            <StatusIndicator type={stateToType(entry.state)} />
          </div>

          {/* Content */}
          <div style={{ flex: 1 }}>
            <SpaceBetween size="xxxs">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box fontWeight="bold">{entry.status.replace(/_/g, ' ')}</Box>
                {entry.phaseDetails && (
                  <Button variant="inline-link" onClick={() => handleToggle(index)}>
                    {expandedIndex === index ? 'Hide' : 'Details'}
                  </Button>
                )}
              </div>
              <Box fontSize="body-s" color="text-body-secondary">
                {formatIsoTimestamp(entry.enteredAt)}
                {entry.statusLatencyMs != null && ` · Duration: ${formatLatency(entry.statusLatencyMs)}`}
                {entry.latencyFromPreviousMs != null && ` · +${formatLatency(entry.latencyFromPreviousMs)}`}
                {entry.cumulativeLatencyMs > 0 && ` · Total: ${formatLatency(entry.cumulativeLatencyMs)}`}
              </Box>
            </SpaceBetween>
          </div>
        </div>
      ))}
    </SpaceBetween>
  );

  const expandedEntry = expandedIndex != null ? timeline[expandedIndex] : null;
  const detailContent = expandedEntry?.phaseDetails ? (
    <PhaseDetailsRenderer rawJson={expandedEntry.phaseDetails} />
  ) : null;

  return (
    <Container header={<Header variant="h2" description={description}>{title}</Header>}>
      {detailContent ? (
        <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
          {timelineList}
          <div>
            <SpaceBetween size="s">
              <Box fontWeight="bold" fontSize="heading-xs">
                {expandedEntry!.status.replace(/_/g, ' ')} — Details
              </Box>
              {detailContent}
            </SpaceBetween>
          </div>
        </Grid>
      ) : timelineList}
    </Container>
  );
};
